import { ChangeDetectionStrategy, Component, DestroyRef, inject, Inject, OnInit, ViewChild } from '@angular/core';
import {
	AlarisEditPanelService,
	AlarisLanguageService,
	AlarisMultiSelectDisplayWithFn,
	AlarisTextAreaComponent,
	CustomValidators,
	EditPanelInputData
} from '@campaign-portal/components-library';
import { ChoiceLocal, Product, Rule, Source } from '@helpers/types/models';
import { RulesService } from '../rules.service';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ProductsService } from '@helpers/repo/products.service';
import { SourcesService } from '../../sources/sources.service';
import { AdditionalValidators, PrefixValidators } from '@helpers/shared/validators';
import { exist, Id } from '@campaign-portal/namespace/common/id';
import { combineLatest, of, pairwise, startWith, switchMap, tap } from 'rxjs';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { eapiEnable } from '../../../app.module';
import { map } from 'rxjs/operators';

@Component({
	selector: 'app-edit-rule',
	templateUrl: './edit-rule.component.html',
	styleUrl: './edit-rule.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class EditRuleComponent implements OnInit {
	@ViewChild('prefixListArea') readonly prefixListArea!: AlarisTextAreaComponent<string>;
	readonly errors: { key: string; value: string }[] = [
		{ key: 'array', value: 'gl.errors.required' },
		{ key: 'mask', value: 'gl.errors.invalid_format' },
		{ key: 'required', value: 'gl.errors.required' },
		{ key: 'sum_shares', value: 'gl.errors.sum_shares' }
	];

	readonly rule: Rule = {
		ruleId: null,
		name: '',
		choiceList: [],
		prefixList: [],
		productList: [],
		productsType: 'hlr',
		productsNames: [],
		senderList: null
	};
	readonly ruleForm = new FormGroup({
		ruleId: new FormControl<number | null>(null),
		name: new FormControl('', { nonNullable: true, validators: Validators.required }),
		productList: new FormControl<string[]>([], Validators.pattern('^\\d+$|(?:\\d+?,?\\d+?)+$')),
		productsType: new FormControl<string>('hlr', { nonNullable: true }),
		productsNames: new FormControl<string[] | null>([]),
		prefixList: new FormControl<string[]>([], {
			nonNullable: true,
			validators: [
				Validators.required,
				Validators.pattern('^\\d{1,99}$|^!?\\d{1,99}((,!?\\d{1,99})|(-\\d{1,99}))+$'),
				PrefixValidators.validatePrefix,
				AdditionalValidators.uniqueArray
			]
		}),
		senderList: new FormControl<string[] | null>(null, [
			AdditionalValidators.elementArray('^([a-zA-Z0-9._-]+)|(\\^.+\\$)$'),
			AdditionalValidators.uniqueArray
		]),
		choiceList: new FormArray<FormControl<any>>([], {
			validators: CustomValidators.requiredArrayOrNull
		})
	});

	products: Product[] = [];
	productsNames: string[] = [];

	readonly productsFilter = new FormControl('');
	readonly productsNamesFilter = new FormControl('');
	readonly inclusiveList = new FormControl<boolean>(false);

	sources: Source[] = [];
	productNamesTypes = ['all', 'hlr', 'sms'];
	LCRVariants = ['Yes', 'No'];
	readonly sourcesFilter = new FormControl();

	readonly separator = 'chr(1) ';

	private afterClone = false;

	private readonly destroyRef = inject(DestroyRef);

	constructor(
		@Inject(EditPanelInputData) private inputData: { rule?: Rule; afterClone?: boolean },
		public rulesService: RulesService,
		public productsService: ProductsService,
		public sourcesService: SourcesService,
		private editPanel: AlarisEditPanelService,
		private lService: AlarisLanguageService
	) {
		if (this.inputData.rule) {
			this.rule = this.inputData.rule;
			if (!this.rule.productList) {
				this.rule.productList = [];
			}
			this.afterClone = this.inputData?.afterClone || false;
		}
	}

	ngOnInit(): void {
		this.reset();

		if (this.ruleForm.get('choiceList')?.value?.length === 0) {
			this.addChoice();
		}

		if (this.afterClone) {
			this.ruleForm.markAsDirty();
			this.inclusiveList.markAsDirty();
		}

		const productsType$ = this.ruleForm.controls.productsType.valueChanges.pipe(
			startWith(this.ruleForm.controls.productsType.value),
			switchMap((value) => {
				if (eapiEnable) {
					return this.productsService.loadNames([value]).pipe(
						map((res) => res.Data ?? [])
					);
				} else {
					return of([]);
				}
			}),
			tap((names) => {
				if (names.length > 0) {
					this.productsNamesFilter.reset();
				}
			})
		);

		const productsNamesFilter$ = this.productsNamesFilter.valueChanges.pipe(
			startWith(this.productsNamesFilter.value ?? ''),
			map((filterValue) => (filterValue ?? '').toLowerCase())
		);

		combineLatest([productsType$, productsNamesFilter$])
			.pipe(
				map(([names, filterValue]) => names.filter((name) => name.toLowerCase().includes(filterValue))),
				tap((filteredNames) => {
					this.productsNames = filteredNames;
				}),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe();

		const productsFilter$ = this.productsFilter.valueChanges.pipe(
			startWith(this.productsFilter.value ?? ''),
			map((filterValue) => (filterValue ?? '').toLowerCase())
		);

		const productList$ = this.ruleForm.controls.productList.valueChanges.pipe(
			startWith(this.ruleForm.controls.productList.value)
		);

		combineLatest([productsFilter$, productList$]).pipe(
			map(([filterValue, productList]: [string, string[] | null]) => {
				const filtered = this.productsService.clientProducts.filter((product) =>
					this.getProductFullName(product).toLowerCase().includes(filterValue)
				);

				if (productList && productList.length > 0) {
					const selectedIdsSet = new Set(productList.map((id) => Number(id)));
					const selectedProducts = filtered.filter((product) =>
						selectedIdsSet.has(Number(product.product_id)));
					const otherProducts = filtered.filter((product) => !selectedIdsSet.has(Number(product.product_id)));
					return [...selectedProducts, ...otherProducts];
				} else {
					return filtered;
				}
			}),
			tap((products) => (this.products = products)),
			takeUntilDestroyed(this.destroyRef)
		).subscribe();

		this.inclusiveList.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
			if (!value) {
				this.ruleForm.controls.productList.patchValue([]);
			}
		});

		this.ruleForm.controls.choiceList.valueChanges
			.pipe(takeUntilDestroyed(this.destroyRef), pairwise())
			.subscribe(([previousValue, currentValue]) => {
				if (currentValue === null) {
					const lastChoice = this.sourcesService.list.find((source) => {
						return !previousValue.includes(source.SourceId);
					});
					if (lastChoice) {
						previousValue.push(lastChoice.SourceId);
					}
					this.ruleForm.controls.choiceList.patchValue(previousValue);
				}
			});

		const sources$ = this.sourcesService.list$.pipe(
			startWith(this.sourcesService.list ?? []),
		);

		const sourcesFilter$ = this.sourcesFilter.valueChanges.pipe(
			startWith(this.sourcesFilter.value ?? '')
		);

		combineLatest([sources$, sourcesFilter$])
			.pipe(
				map(([list, filterValue]) =>
					list.filter((source) =>
						source.Name.includes(filterValue ?? '')
					)
				),
				tap((filteredSources) => {
					this.sources = filteredSources;
					this.ruleForm.updateValueAndValidity();
					this.sourcesFilter.reset();
				}),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe();
	}

	OnChange(): void {
		const productList: string[] | null = this.ruleForm.controls.productList.value;
		if (productList && productList.length > 0) {
			const selectedIdsSet = new Set(productList.map((id) => Number(id)));
			const filtered = this.productsService.clientProducts.filter((item) => !selectedIdsSet.has(item.product_id));
			const selectedProducts = productList
				.map((id) => this.productsService.map.get(Number(id)))
				.filter((p): p is Product => p !== undefined);
			this.products = [...selectedProducts, ...filtered];
		} else {
			this.products = this.productsService.list;
		}
	}

	OnChangeEnableLCR(i: number): void {
		const sources = (this.ruleForm.get('choiceList') as FormArray)
			.at(i)
			.get('sources') as FormArray;
		if (this.ruleForm.controls?.choiceList?.at(i)?.value['enableLCR'] === 'Yes'
			&& sources.length >= 1) {
			sources.clear();
			sources.push(
				new FormGroup({
					Source: new FormControl<string[]>([]),
					Share: new FormControl<number | null>(sources.length === 0 ? 100 : null)
				})
			);
		} else {
			sources.clear();
			sources.push(
				new FormGroup({
					Source: new FormControl<string[]>([], Validators.required),
					Share: new FormControl<number | null>(sources.length === 0 ? 100 : null)
				})
			);
		}
	}

	getEnableLCR(i: number): string {
		return this.ruleForm.controls?.choiceList?.at(i)?.value.enableLCR;
	}

	getSharesValue(i: number): number {
		return this.ruleForm.controls?.choiceList?.at(i)?.value.sources.length;
	}

	displayProduct: AlarisMultiSelectDisplayWithFn<Id<exist>> = (products: Id<exist>[] | null): string => {
		if (products) {
			return products.length === 0
				? ''
				: products
					.map(
						(product) =>
							this.productsService.map.get(Number(product))?.product_caption +
							' (' + this.productsService.map.get(Number(product))?.product_id + ')'
					)
					.join(this.separator);
		}

		return this.lService.translate('gl.all');
	};

	displayChoices: AlarisMultiSelectDisplayWithFn<string> = (sources: string[] | null): string => {
		if (sources) {
			const newSources = sources.map(this.checkHuntstop.bind(this));
			return newSources.length === 0 ? '' : newSources.join(this.separator);
		}
		return '';
	};

	displayChoice = (source: string | null): string => {
		if (source) {
			return this.checkHuntstop(source);
		}
		return '';
	};

	private checkHuntstop(source: string): string {
		const sourceStr = source.toString();
		const huntResult = this.huntStop(sourceStr);
		return huntResult === 'Huntstop' ? `${sourceStr} ${huntResult}` : sourceStr;
	}

	reset(): void {
		const rule = structuredClone(this.rule);
		rule.productList = rule.productList
			?.map(Number)
			.sort((a, b) => a - b)
			.map(String) as string[] | null;
		this.createChoiceControlsFromData(this.rule.choiceList as ChoiceLocal[]);
		this.ruleForm.reset(rule);
		this.inclusiveList.reset(true);
		if (rule.productList && rule.productList?.length > 0) {
			this.inclusiveList.setValue(true);
		} else {
			this.inclusiveList.setValue(false);
		}
	}

	createChoiceControlsFromData(choiceList: ChoiceLocal[]): void {
		(this.ruleForm.get('choiceList') as FormArray).clear();
		for (const list of choiceList) {
			this.addChoice(false, list);
			if ((list.sources.length && list.enableLCR === 'No') || list.enableLCR === 'Yes') {
				for (let i = 0; i < (list.sources.length > 0 ? list.sources.length : 1); i++) {
					const choiceL = this.ruleForm.get('choiceList') as FormArray;
					this.addSource(choiceL.length - 1, choiceList);
				}
			} else {
				this.addSource(choiceList.length - 1, choiceList);
			}
		}
	}

	save(): void {
		const form = this.ruleForm.controls;
		const inclusive = this.inclusiveList.value;
		const productListValue = form.productList.value;

		const productList = inclusive
			? productListValue === null
				? this.productsService.list.map((p) => p.product_id.toString())
				: productListValue.length
					? productListValue : null
			: null;

		const choiceListValue = form.choiceList.value;
		const choiceList = choiceListValue === null ? this.sourcesService.list.map((s) => s.Name) : choiceListValue;

		const rule: Rule = {
			ruleId: this.rule.ruleId,
			name: form.name.value,
			productList,
			productsType: form.productsType.value,
			productsNames: form.productsNames.value,
			prefixList: form.prefixList.value,
			senderList: form.senderList.value,
			choiceList
		};

		rule.choiceList.forEach((choice) => {
			choice.sources.forEach((source) => {
				if (typeof source.Source === 'string') {
					source.Source = [source.Source];
				}
			});
		});

		if (rule.productList && eapiEnable) {
			rule.productList = rule.productList.map(String);
		}

		this.rulesService
			.update(rule)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((resp) => {
				if (resp.data?.rule_id) {
					this.editPanel.close(true);
				}
			});
	}

	close(): void {
		this.editPanel.close(false);
	}

	drop(event: CdkDragDrop<Id<exist>>): void {
		const choiceList = this.ruleForm.controls.choiceList.value;
		moveItemInArray(choiceList, event.previousIndex, event.currentIndex);
		this.createChoiceControlsFromData(choiceList);
		this.ruleForm.controls.choiceList.setValue(choiceList);
		this.ruleForm.controls.choiceList.markAsDirty();
		this.inclusiveList.markAsDirty();
	}

	protected readonly eapiEnable = eapiEnable;

	addChoice(addSource = true, choice?: ChoiceLocal): void {
		const choiceList = this.ruleForm.get('choiceList') as FormArray;
		choiceList.push(
			new FormGroup({
				sources: new FormArray([], choice?.enableLCR === 'Yes'?
					{} : { validators: AdditionalValidators.sumShares}),
				enableLCR: new FormControl<string>('No')
			})
		);
		if (addSource) {
			this.addSource(choiceList.length - 1);
		}
	}

	addSource(index: number, choiceList?: ChoiceLocal[]): void {
		const sources = (this.ruleForm.get('choiceList') as FormArray).at(index).get('sources') as FormArray;
		const enableLCR = choiceList?.at(index)?.enableLCR;
		const fg = new FormGroup({
			Source: new FormControl<string[]>([],
				enableLCR === 'Yes'? {} : { validators: Validators.required }),
			Share: new FormControl<number | null>(sources.length === 0 ? 100 : null)
		});
		if (sources.length === 1) {
			const remainingControl = sources.at(0).get('Share');
			if (remainingControl && remainingControl.value === 100) {
				remainingControl.setValue(null);
			}
		}
		sources.push(fg);
	}

	getSources(index: number): FormArray {
		return (this.ruleForm.get('choiceList') as FormArray).at(index).get('sources') as FormArray;
	}

	getProductFullName(product: Product): string {
		return (
			product.product_caption +
			'(' +
			product.acc_currency_code +
			')' +
			'(' +
			product.product_id +
			')' +
			'-' +
			(product.product_direction === 0 ? 'Client' : 'Vendor').toString()
		);
	}

	removeSource(i: number, j: number): void {
		const sources = this.getSources(i);
		sources.removeAt(j);
		if (sources.length === 1) {
			const remainingControl = sources.at(0).get('Share');
			if (remainingControl && remainingControl.value !== 100) {
				remainingControl.setValue(100);
			}
		}
		this.ruleForm.controls.choiceList.markAsDirty();
		this.inclusiveList.markAsDirty();
	}

	removeChoice(i: number): void {
		(this.ruleForm.get('choiceList') as FormArray).removeAt(i);
		this.ruleForm.controls.choiceList.markAsDirty();
		this.inclusiveList.markAsDirty();
	}

	huntStop(name: string): string {
		return this.sources.filter((source) => source.Name === name
			&& source.HuntStop !== 'DISABLED').length > 0 ? 'Huntstop' : '';
	}

	getAvailableSources(currentChoiceIndex: number): Source<number | null>[] {
		const choices = (this.ruleForm.get('choiceList') as FormArray).value as ChoiceLocal[];

		const selectedInOtherChoices = choices
			.filter((_, index) => index !== currentChoiceIndex)
			.flatMap(choice => choice.sources.map(el => el.Source).flat());

		return this.sources.filter((source) => !selectedInOtherChoices.includes(source.Name));
	}

	isRegexp(str: string): boolean {
		return str[0] === '^' && str[str.length - 1] === '$';
	}

	getRegexp(value: string[]): { value: string; originalIndex: number }[] {
		return value
			.map((el, index) => {
				return { value: el, originalIndex: index };
			})
			.filter((el) => this.isRegexp(el.value));
	}

	getNotRegexp(value: string[]): { value: string; originalIndex: number }[] {
		return value
			.map((el, index) => {
				return { value: el, originalIndex: index };
			})
			.filter((el) => !this.isRegexp(el.value));
	}
}
